/**
 * useValuationFormState Hook
 * Manages financial data, settings, and localStorage + API persistence for valuation forms
 */
import { useState, useEffect, useRef } from 'react';
import logger from '../utils/logger';
import { useValuationAuth } from './useValuationAuth';
import { useFinancialDataAPI } from './useFinancialDataAPI';

// Utility functions for number formatting
const formatNumberWithCommas = (value) => {
  if (!value && value !== 0) return '';
  const numStr = value.toString().replace(/[^\d.-]/g, '');
  const parts = numStr.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const parseNumberFromFormatted = (value) => {
  if (!value) return '';
  return value.toString().replace(/,/g, '');
};

export const useValuationFormState = (businessId = null) => {
  const { getUserId } = useValuationAuth();
  const [currentYear] = useState(new Date().getFullYear());
  const years = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
  const contentScrollRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const [hasLoadedFromAPI, setHasLoadedFromAPI] = useState(false);

  // Initialize API hook
  const {
    loadFinancialData,
    saveFinancialData,
    loading: apiLoading,
    error: apiError,
    lastSaved
  } = useFinancialDataAPI(businessId);

  // Method selection and settings
  const [selectedMethod, setSelectedMethod] = useState(() => {
    if (!businessId) return 'cca';
    return localStorage.getItem(`advancedValuationMethod_${businessId}`) || 'cca';
  });

  const [privateDiscount, setPrivateDiscount] = useState(() => {
    if (!businessId) return 25;
    return parseFloat(localStorage.getItem(`advancedValuationDiscount_${businessId}`)) || 25;
  });

  const [businessType, setBusinessType] = useState(() => {
    if (!businessId) return '';
    return localStorage.getItem(`advancedValuationBusinessType_${businessId}`) || '';
  });

  const [ccaMultipleType, setCcaMultipleType] = useState(() => {
    if (!businessId) return 'ev_ebitda';
    return localStorage.getItem(`advancedValuationCcaMultipleType_${businessId}`) || 'ev_ebitda';
  });

  const [capitalizationRate, setCapitalizationRate] = useState(() => {
    if (!businessId) return '';
    return localStorage.getItem(`advancedValuationCapRate_${businessId}`) || '';
  });

  const [capitalizationEarningsType, setCapitalizationEarningsType] = useState(() => {
    if (!businessId) return 'ebitda';
    return localStorage.getItem(`advancedValuationCapEarningsType_${businessId}`) || 'ebitda';
  });

  const [manualMultipleType, setManualMultipleType] = useState(() => {
    if (!businessId) return 'ev_ebitda';
    return localStorage.getItem(`advancedValuationManualMultipleType_${businessId}`) || 'ev_ebitda';
  });

  const [manualMultiple, setManualMultiple] = useState(() => {
    if (!businessId) return '';
    return localStorage.getItem(`advancedValuationManualMultiple_${businessId}`) || '';
  });

  // Method-specific inputs (DCF settings, etc.)
  const [methodInputs, setMethodInputs] = useState(() => {
    if (!businessId) {
      return {
        dcf_perpetuity: false,
        dcf_years: 5,
        dcf_discount_rate: 15,
        dcf_annual_growth_rate: 5,
        dcf_growth_rate: 3
      };
    }
    const saved = localStorage.getItem(`advancedValuationMethodInputs_${businessId}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        logger.error('Error parsing method inputs', { error: e });
      }
    }
    return {
      dcf_perpetuity: false,
      dcf_years: 5,
      dcf_discount_rate: 15,
      dcf_annual_growth_rate: 5,
      dcf_growth_rate: 3
    };
  });

  // Create initial financial data structure
  const createInitialFinancialData = (savedData = null) => {
    const fieldNames = [
      'revenue', 'cogs', 'gross_profit', 'operating_expenses', 'ebitda',
      'depreciation', 'ebit', 'tax_expense', 'profit_after_tax', 'sde',
      'total_assets', 'total_liabilities_equity', 'owner_compensation',
      'non_recurring_expenses', 'capital_expenditures', 'cash',
      'current_assets', 'non_current_assets', 'long_term_debt',
      'short_term_debt', 'interest_bearing_debt', 'equity', 'total_liabilities',
      'owner_salary', 'family_salaries', 'salary_adjustments', 'auto_expenses',
      'donations', 'insurance_premiums', 'inventory_adjustments',
      'meals_entertainment', 'personal_expenses', 'phone_internet',
      'non_business_services', 'travel', 'one_time_charges', 'retirement',
      'supplier_rebates', 'other_addbacks', 'addbacks_total',
      'capex', 'working_capital_change', 'interest_expense', 'free_cash_flow'
    ];

    const data = {};
    fieldNames.forEach(field => {
      data[field] = years.reduce((acc, year) => ({
        ...acc,
        [year]: savedData?.[field]?.[year] || ''
      }), {});
    });

    return data;
  };

  // Financial data state
  const [financialData, setFinancialData] = useState(() => {
    if (!businessId) {
      return createInitialFinancialData();
    }
    const saved = localStorage.getItem(`advancedValuationData_${businessId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return createInitialFinancialData(parsed);
      } catch (e) {
        logger.error('Error loading saved valuation data', { error: e });
      }
    }
    return createInitialFinancialData();
  });

  // UI state
  const [activeTab, setActiveTab] = useState('settings');
  const [showMethodModal, setShowMethodModal] = useState(false);
  const [showDiscountInfo, setShowDiscountInfo] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [helpContent, setHelpContent] = useState({ title: '', content: '' });
  const [sliderCalculating, setSliderCalculating] = useState(false);
  const [justRecalculated, setJustRecalculated] = useState(false);
  const debounceTimeout = useRef(null);

  // Handle input changes with number formatting
  const handleInputChange = (field, year, value) => {
    const cleanValue = parseNumberFromFormatted(value);

    setFinancialData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [year]: cleanValue
      }
    }));
  };

  // Save financial data to localStorage
  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationData_${businessId}`, JSON.stringify(financialData));
  }, [financialData, businessId]);

  // Save method inputs to localStorage
  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationMethodInputs_${businessId}`, JSON.stringify(methodInputs));
  }, [methodInputs, businessId]);

  // Save individual settings to localStorage
  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationMethod_${businessId}`, selectedMethod);
  }, [selectedMethod, businessId]);

  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationDiscount_${businessId}`, privateDiscount.toString());
  }, [privateDiscount, businessId]);

  useEffect(() => {
    if (!businessId) return;
    if (businessType) localStorage.setItem(`advancedValuationBusinessType_${businessId}`, businessType);
  }, [businessType, businessId]);

  useEffect(() => {
    if (!businessId) return;
    if (capitalizationRate) localStorage.setItem(`advancedValuationCapRate_${businessId}`, capitalizationRate);
  }, [capitalizationRate, businessId]);

  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationCapEarningsType_${businessId}`, capitalizationEarningsType);
  }, [capitalizationEarningsType, businessId]);

  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationManualMultipleType_${businessId}`, manualMultipleType);
  }, [manualMultipleType, businessId]);

  useEffect(() => {
    if (!businessId) return;
    if (manualMultiple) localStorage.setItem(`advancedValuationManualMultiple_${businessId}`, manualMultiple);
  }, [manualMultiple, businessId]);

  useEffect(() => {
    if (!businessId) return;
    localStorage.setItem(`advancedValuationCcaMultipleType_${businessId}`, ccaMultipleType);
  }, [ccaMultipleType, businessId]);

  // Reset state when businessId changes (critical for data isolation)
  useEffect(() => {
    if (!businessId) return;

    // Reset financial data to initial state
    setFinancialData(createInitialFinancialData());

    // Load settings from localStorage for this business
    setSelectedMethod(localStorage.getItem(`advancedValuationMethod_${businessId}`) || 'cca');
    setPrivateDiscount(parseFloat(localStorage.getItem(`advancedValuationDiscount_${businessId}`)) || 25);
    setBusinessType(localStorage.getItem(`advancedValuationBusinessType_${businessId}`) || '');
    setCcaMultipleType(localStorage.getItem(`advancedValuationCcaMultipleType_${businessId}`) || 'ev_ebitda');
    setCapitalizationRate(localStorage.getItem(`advancedValuationCapRate_${businessId}`) || '');
    setCapitalizationEarningsType(localStorage.getItem(`advancedValuationCapEarningsType_${businessId}`) || 'ebitda');
    setManualMultipleType(localStorage.getItem(`advancedValuationManualMultipleType_${businessId}`) || 'ev_ebitda');
    setManualMultiple(localStorage.getItem(`advancedValuationManualMultiple_${businessId}`) || '');

    const savedMethodInputs = localStorage.getItem(`advancedValuationMethodInputs_${businessId}`);
    if (savedMethodInputs) {
      try {
        setMethodInputs(JSON.parse(savedMethodInputs));
      } catch (e) {
        logger.error('Error parsing method inputs', { error: e });
      }
    }

    const savedFinancialData = localStorage.getItem(`advancedValuationData_${businessId}`);
    if (savedFinancialData) {
      try {
        const parsed = JSON.parse(savedFinancialData);
        setFinancialData(createInitialFinancialData(parsed));
      } catch (e) {
        logger.error('Error loading saved valuation data', { error: e });
      }
    }

    // Reset API loaded flag to trigger fresh API load
    setHasLoadedFromAPI(false);
  }, [businessId]);

  // Cleanup debounce timeout
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Load data from API on mount (if businessId is provided)
  useEffect(() => {
    if (businessId && !hasLoadedFromAPI) {
      logger.info('[API] Loading financial data for business:', businessId);
      loadFinancialData().then(data => {
        if (data && data.exists) {
          // Load settings from API
          if (data.settings) {
            if (data.settings.valuation_method) setSelectedMethod(data.settings.valuation_method);
            if (data.settings.private_discount !== null) setPrivateDiscount(data.settings.private_discount);
            if (data.settings.business_type) setBusinessType(data.settings.business_type);
            if (data.settings.cca_multiple_type) setCcaMultipleType(data.settings.cca_multiple_type);
            if (data.settings.capitalization_rate) setCapitalizationRate(data.settings.capitalization_rate);
            if (data.settings.capitalization_earnings_type) setCapitalizationEarningsType(data.settings.capitalization_earnings_type);
            if (data.settings.manual_multiple_type) setManualMultipleType(data.settings.manual_multiple_type);
            if (data.settings.manual_multiple) setManualMultiple(data.settings.manual_multiple);
            if (data.settings.method_inputs) setMethodInputs(data.settings.method_inputs);
          }

          // Load financial data from API
          if (data.financial_data) {
            setFinancialData(data.financial_data);
          }

          logger.info('Loaded financial data from API', { businessId });
        } else {
          // API returned no data (database is empty) - clear localStorage and reset state
          logger.info('[API] No data in database - clearing localStorage and resetting state', { businessId });

          // Clear all localStorage for this business
          localStorage.removeItem(`advancedValuationData_${businessId}`);
          localStorage.removeItem(`advancedValuationMethodInputs_${businessId}`);
          localStorage.removeItem(`advancedValuationMethod_${businessId}`);
          localStorage.removeItem(`advancedValuationDiscount_${businessId}`);
          localStorage.removeItem(`advancedValuationBusinessType_${businessId}`);
          localStorage.removeItem(`advancedValuationCcaMultipleType_${businessId}`);
          localStorage.removeItem(`advancedValuationCapRate_${businessId}`);
          localStorage.removeItem(`advancedValuationCapEarningsType_${businessId}`);
          localStorage.removeItem(`advancedValuationManualMultipleType_${businessId}`);
          localStorage.removeItem(`advancedValuationManualMultiple_${businessId}`);

          // Reset all state to defaults
          setFinancialData(createInitialFinancialData());
          setSelectedMethod('cca');
          setPrivateDiscount(25);
          setBusinessType('');
          setCcaMultipleType('ev_ebitda');
          setCapitalizationRate('');
          setCapitalizationEarningsType('ebitda');
          setManualMultipleType('ev_ebitda');
          setManualMultiple('');
          setMethodInputs({
            dcf_perpetuity: false,
            dcf_years: 5,
            dcf_discount_rate: 15,
            dcf_annual_growth_rate: 5,
            dcf_growth_rate: 3
          });
        }
        setHasLoadedFromAPI(true);
      }).catch(err => {
        logger.error('Failed to load financial data from API', { error: err });
        setHasLoadedFromAPI(true);
      });
    }
  }, [businessId, hasLoadedFromAPI, loadFinancialData]);

  // Autosave to API when data changes (debounced)
  useEffect(() => {
    if (!businessId || !hasLoadedFromAPI) {
      logger.info('[API] Skipping autosave - businessId:', businessId, 'hasLoadedFromAPI:', hasLoadedFromAPI);
      return;
    }

    logger.info('[API] Data changed, setting up autosave timer...');

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout to save after 2 seconds of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      logger.info('[API] Autosaving financial data for business:', businessId);
      const dataToSave = {
        years,
        settings: {
          valuation_method: selectedMethod,
          private_discount: privateDiscount,
          business_type: businessType,
          cca_multiple_type: ccaMultipleType,
          capitalization_rate: capitalizationRate,
          capitalization_earnings_type: capitalizationEarningsType,
          manual_multiple_type: manualMultipleType,
          manual_multiple: manualMultiple,
          method_inputs: methodInputs
        },
        financial_data: financialData
      };

      saveFinancialData(dataToSave).then(success => {
        if (success) {
          logger.info('[API] ✓ Data saved successfully');
        } else {
          logger.error('[API] ✗ Data save failed');
        }
      }).catch(err => {
        logger.error('[API] Failed to autosave financial data', { error: err });
      });
    }, 2000); // 2 second debounce

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    businessId,
    hasLoadedFromAPI,
    years,
    selectedMethod,
    privateDiscount,
    businessType,
    ccaMultipleType,
    capitalizationRate,
    capitalizationEarningsType,
    manualMultipleType,
    manualMultiple,
    methodInputs,
    financialData,
    saveFinancialData
  ]);

  return {
    // Data
    years,
    financialData,
    selectedMethod,
    privateDiscount,
    businessType,
    ccaMultipleType,
    capitalizationRate,
    capitalizationEarningsType,
    manualMultipleType,
    manualMultiple,
    methodInputs,

    // UI State
    activeTab,
    showMethodModal,
    showDiscountInfo,
    showHelpModal,
    showCalculationModal,
    helpContent,
    sliderCalculating,
    justRecalculated,
    contentScrollRef,
    debounceTimeout,

    // Setters
    setFinancialData,
    setSelectedMethod,
    setPrivateDiscount,
    setBusinessType,
    setCcaMultipleType,
    setCapitalizationRate,
    setCapitalizationEarningsType,
    setManualMultipleType,
    setManualMultiple,
    setMethodInputs,
    setActiveTab,
    setShowMethodModal,
    setShowDiscountInfo,
    setShowHelpModal,
    setShowCalculationModal,
    setHelpContent,
    setSliderCalculating,
    setJustRecalculated,

    // Handlers
    handleInputChange,

    // Utilities
    formatNumberWithCommas,
    parseNumberFromFormatted,

    // API State
    apiLoading,
    apiError,
    lastSaved,
    hasLoadedFromAPI
  };
};
